import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { IncomeFrequency, IncomeOptionsDTO, IncomeType } from '../../api/useIncomeQuery';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { getIncomeOwnerModel } from '../../Income.meta';

interface EmploymentFormFieldsProps {
  formBag: any;
  options: IncomeOptionsDTO;
  fieldNamePrefix?: string;
}

export const EmploymentFormFields = ({
  formBag: { register, control, setValue, getValues, watch },
  fieldNamePrefix = '',
  options,
}: EmploymentFormFieldsProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { PrincipalAgesOptions, SpouseAgesOptions } = options;

  const earnerOptions = Object.keys(options.OwnerOptions).map(key => ({
    label: key,
    options: (options.OwnerOptions[key] || []).map((owner, ind) => ({
      value: owner.Name,
      label: owner.Name,
    })),
  }));

  const earnerFieldName = `${fieldNamePrefix}Earner`;

  const earner = watch(earnerFieldName);
  const startAge = watch(`${fieldNamePrefix}StartAge`);
  const isEarnerSpouse = options.OwnerOptions.People[1]?.Name === earner;

  const incomeFrequencyOptions = [
    { label: 'Yearly', value: IncomeFrequency.Yearly },
    { label: 'Monthly', value: IncomeFrequency.Monthly },
  ];

  const ageOptions = (isEarnerSpouse ? SpouseAgesOptions : PrincipalAgesOptions).map(
    ({ Age, Label: label, SubLabel: rightLabel }) => ({
      value: Age,
      label,
      rightLabel,
    }),
  );

  const startAgeOptions = [{ value: null, label: 'Start Today' }, ...ageOptions];

  const endAgeOptions = [
    { value: null, label: 'At Retirement' },
    ...ageOptions.filter(opt => opt.value >= startAge),
  ];

  const [managementStyleValue] = watch(['OrionManagementStyleId']);

  return (
    <>
      <Select
        label="Income Type"
        placeholder="Select Income Type"
        value={{ label: 'Employment Income', value: IncomeType.Salary }}
        isPortal
        disabled
      />

      <Input label="Name" {...register(`${fieldNamePrefix}Name`)} />

      <Controller
        name={earnerFieldName}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Earner"
            value={earnerOptions
              .map(({ options }) => options)
              .flat()
              .find(({ value: v }) => v === value)}
            options={earnerOptions}
            onChange={option => {
              onChange(option?.value);

              setValue(`${fieldNamePrefix}StartAge`, null);
              setValue(`${fieldNamePrefix}EndAge`, null);
              if (option) {
                setValue(
                  `${fieldNamePrefix}OwnerModel`,
                  getIncomeOwnerModel({ options: options.OwnerOptions, value: option.value }),
                );
              }
            }}
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name={`${fieldNamePrefix}Amount`}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            allowNegative={false}
            label="Salary"
            leftIcon="far fa-dollar-sign"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            selectorProps={{
              defaultValue:
                incomeFrequencyOptions.find(
                  ({ value }) => value === getValues(`${fieldNamePrefix}IncomeFrequency`),
                ) || incomeFrequencyOptions[0],
              options: incomeFrequencyOptions,
              onChange: option => setValue(`${fieldNamePrefix}IncomeFrequency`, option.value),
              ariaLabel: 'Income frequency selector',
            }}
            decimalScale={2}
            tooltip="Enter your total income before deducting taxes, benefits, FICA, contributions, etc. in today's dollars."
            {...field}
          />
        )}
      />

      <Controller
        control={control}
        name={`${fieldNamePrefix}BonusCommissions`}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Bonus/Commission"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            {...field}
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={`${fieldNamePrefix}OrionManagementStyleId`}
        onChange={(value: number | null) => {
          setValue(`${fieldNamePrefix}CustomBucketIndex`, value);
        }}
      />

      <AssetBucket
        control={control}
        fieldName={`${fieldNamePrefix}CustomBucketIndex`}
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            `}
          >
            <Controller
              name={`${fieldNamePrefix}StartAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Starts at Age"
                  value={startAgeOptions.find(({ value: v }) => v === value)}
                  options={startAgeOptions}
                  onChange={option => {
                    onChange(option?.value);
                    setValue(`${fieldNamePrefix}EndAge`, null);
                  }}
                  tooltip="Enter the age of the Earner when the income will start. To start this income immediately, leave this field blank."
                  isPortal
                />
              )}
            />

            <Controller
              name={`${fieldNamePrefix}EndAge`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Ends at Age"
                  value={endAgeOptions.find(({ value: v }) => v === value)}
                  options={endAgeOptions}
                  onChange={option => {
                    onChange(option?.value);
                    setValue(`${fieldNamePrefix}EndAtRetirement`, option?.value === null);
                  }}
                  tooltip="Enter the age of the Earner when the income will stop. To stop this income at retirement, leave this field blank."
                  isPortal
                />
              )}
            />
          </Box>

          <Controller
            control={control}
            name={`${fieldNamePrefix}Inflation`}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Salary field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
                {...field}
              />
            )}
          />

          <Toggle
            {...register(`${fieldNamePrefix}IsTaxFree`)}
            size="small"
            helperText="If checked, this income will be free of federal and state taxes (e.g. Military combat pay). By default employment incomes are taxed."
          >
            Tax Free
          </Toggle>

          <Toggle
            {...register(`${fieldNamePrefix}SelfEmployed`)}
            size="small"
            helperText="Applies SECA tax and deducts half the amount from pre-tax profit."
          >
            SECA Tax
          </Toggle>
        </>
      )}
    </>
  );
};
